import { SyncOutlined } from "@ant-design/icons";
import { Editor } from "@monaco-editor/react";
import { DeleteOutline } from "@mui/icons-material";
import { useAuthHeaders } from "@src/frontend/hooks/useAuthHeaders";
import { useMessageHandler } from "@src/frontend/hooks/useMessageHandler";
import { useRestAPI } from "@src/frontend/hooks/useRestAPI";
import { useUserClaims } from "@src/frontend/hooks/useUserClaims";
import { useListDataScore } from "@src/frontend/stores/listDataScore";
import type { Res } from "@src/frontend/utils/types";
import type { BrowserSessionModel, ScriptResultModel, UserScriptModel } from "@src/generated/prisma/models";
import { useRequest } from "ahooks";
import { Flex, Form, Select, Typography, Button, Drawer, Table, Tag, Space, Popconfirm } from "antd";
import dayjs from "dayjs";
import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";

export const ScriptResultViewPage = () => {
  const location = useLocation();
  const { script_id, session_id } = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const script_id = searchParams.get('script_id');
    const session_id = searchParams.get('session_id');
    return { script_id, session_id };
  }, [location]);

  const [scriptId, setScriptId] = useState(script_id);
  const [sessionId, setSessionId] = useState(session_id);
  const [selectedResult, setSelectedResult] = useState<ScriptResultModel>();

  const { getList, listMap } = useListDataScore(store => store);

  useRestAPI('/api/sessions');
  useRestAPI('/api/scripts');
  useRestAPI('/api/scripts?publish=true');

  const options = useMemo(() => {
    const browserSessions = getList<BrowserSessionModel>('/api/sessions') ?? [];
    const userScriptsPri = getList<UserScriptModel>('/api/scripts') ?? [];
    const priActions = new Set(userScriptsPri.map(e => e.id));
    const userScriptsPub = getList<UserScriptModel>('/api/scripts?publish=true').filter(e => {
      return !priActions.has(e.id);
    });
    return {
      session: browserSessions,
      script: [...userScriptsPri, ...userScriptsPub],
    };
  }, [getList, listMap]);
  const scriptMap = useMemo(() => {
    return Object.fromEntries(options.script.map(e => [e.id, e]));
  }, [options]);
  const sessionMap = useMemo(() => {
    return Object.fromEntries(options.session.map(e => [e.id, e]));
  }, [options]);

  const { handleMessage } = useMessageHandler();
  const { getAuthHeader } = useAuthHeaders();
  const listReq = useRequest<Res<ScriptResultModel[]>, []>(async () => {
    const headers = await getAuthHeader();
    if (!headers) return null;
    const params = new URLSearchParams();
    if (scriptId) params.append('script_id', scriptId);
    if (sessionId) params.append('session_id', sessionId);
    return fetch(`/api/script-result?${params}`, { headers })
    .then(res => res.json())
    .then(data => handleMessage(data))
    .catch(e => handleMessage(e));
  }, { refreshDeps: [scriptId, sessionId, getAuthHeader], debounceWait: 300 })
  const delReq = useRequest(async (unique_key: string) => {
    const headers = await getAuthHeader();
    if (!headers) return;
    return fetch(`/api/script-result`, { headers, method: 'DELETE', body: JSON.stringify({ unique_key }) })
      .then(res => res.json())
      .then(data => handleMessage(data))
      .catch(e => handleMessage(e));
  }, { manual: true, refreshDeps: [getAuthHeader, handleMessage] });
  const [user] = useUserClaims();

  return (
    <Flex className="page-shell" vertical gap={24}>
      <Flex className="page-header" vertical>
        <Typography.Text className="page-eyebrow">ACTIVITY</Typography.Text>
        <Typography.Title level={2} className="page-title">运行记录</Typography.Title>
        <Typography.Text type="secondary">检索并检查自动化脚本返回的结构化结果。</Typography.Text>
      </Flex>
      <Form layout="horizontal" className="section-block">
        <Flex wrap gap={24}>
          <Form.Item label="浏览器">
            <Select
              allowClear
              style={{ width: 320 }}
              value={sessionId}
              onChange={setSessionId}
              options={options.session.map(e => ({ label: e.name, value: e.id }))}
            />
          </Form.Item>
          <Form.Item label="脚本">
            <Select
              allowClear
              style={{ width: 320 }}
              value={scriptId}
              onChange={setScriptId}
              options={options.script.map(e => ({ label: e.name, value: e.id }))}
            />
          </Form.Item>
          <Button onClick={listReq.runAsync}>
            <SyncOutlined />
          </Button>
        </Flex>
      </Form>
      <Table
        rowKey="unique_key"
        loading={listReq.loading}
        dataSource={listReq.data?.data ?? []}
        pagination={{ pageSize: 20, showSizeChanger: true }}
        scroll={{ x: 900 }}
        onRow={record => ({ onClick: () => setSelectedResult(record) })}
        columns={[
          { width: 190, title: '执行时间', dataIndex: 'created_at', sorter: (a, b) => dayjs(a.created_at).valueOf() - dayjs(b.created_at).valueOf(), defaultSortOrder: 'descend', render: value => dayjs(value).format('YYYY-MM-DD HH:mm:ss') },
          { width: 220, title: '脚本', dataIndex: 'script_id', render: value => scriptMap[value]?.name || <Typography.Text type="secondary">未知脚本</Typography.Text> },
          { width: 220, title: '浏览器会话', dataIndex: 'session_id', render: value => sessionMap[value]?.name || <Typography.Text type="secondary">未知会话</Typography.Text> },
          { width: 100, title: '状态', render: () => <Tag color="success">已完成</Tag> },
          { title: '备注', dataIndex: 'remark', ellipsis: true, render: value => value || '-' },
          { fixed: 'right', width: 150, align: 'right', title: '操作', render: (_, record) => <Space onClick={event => event.stopPropagation()}>
            <Button type="link" onClick={() => setSelectedResult(record)}>查看</Button>
            <Popconfirm title="删除这条运行记录？" description={user?.sub === record.logto_uid ? null : `该日志属于 ${record.logto_uid}`} onConfirm={async () => { await delReq.runAsync(record.unique_key); await listReq.runAsync(); }}><Button danger type="text" icon={<DeleteOutline />} /></Popconfirm>
          </Space> },
        ]}
      />
      <Drawer title="运行结果详情" size={720} open={Boolean(selectedResult)} onClose={() => setSelectedResult(undefined)}>
        {selectedResult && <Flex vertical gap={18}>
          <Flex gap={24} wrap>
            <Typography.Text type="secondary">脚本：<Typography.Text>{scriptMap[selectedResult.script_id]?.name || '未知脚本'}</Typography.Text></Typography.Text>
            <Typography.Text type="secondary">会话：<Typography.Text>{sessionMap[selectedResult.session_id]?.name || '未知会话'}</Typography.Text></Typography.Text>
            <Typography.Text type="secondary">时间：<Typography.Text>{dayjs(selectedResult.created_at).format('YYYY-MM-DD HH:mm:ss')}</Typography.Text></Typography.Text>
          </Flex>
          {selectedResult.remark && <Typography.Paragraph>{selectedResult.remark}</Typography.Paragraph>}
          <Editor defaultLanguage="json" height="calc(100vh - 210px)" theme="vs-dark" value={JSON.stringify(selectedResult.result, null, 2)} options={{ minimap: { enabled: false }, readOnly: true, wordWrap: 'on', scrollBeyondLastLine: false, automaticLayout: true }} />
        </Flex>}
      </Drawer>
    </Flex>
  );
}
